import React, { useEffect, useRef, useState } from 'react';
import './VisitPage.scss';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router';
import {
  getFilterData,
  getJsonFromStorage,
  getPaginationOpts,
  responseLogWithNotification,
  useUpdatePeriod,
} from '../../../utils/utils';
import { getSystemData } from '../../System/reducer';
import { getVisitData } from '../reducer';
import Pagination from '../../../components/base/Pagination/Pagination';
import CustomizedGrid from '../../../components/base/Grid/CustomizedGrid';
import { VISITS_MODULE } from '../../../configs/moduleNames';
import { loadVisitsList } from '../actions';
import { visitColumns } from './visitsColumns';
import FilterRow from '../../../components/base/FilterRow/FilterRow';
import FieldDTO from '../../../components/fields/Abstract/FieldDTO';
import { Icons, Button } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import TextInput from '../../../components/fields/TextInput/TextInput';
import CustomSelectInput from '../../../components/fields/SelectInput/CustomSelectInput';
import RangeDatePicker from '../../../components/fields/RangeDatePicker/RangeDatePicker';
import { POPUP_LIST_NAME, VEHICLE_REGISTRY_PRESENCE_LIST } from '../../../configs';
import { addGlobalPopUpWrapper, closeAllPopupWrapper } from '../../InformationScreenWrapper/actions';
import getDefaultSort from '../../../utils/getDefaultSort';
import FilterModal from '../../FilterTemplate/components/FilterModal';
import { PAGE_TYPE } from '../../FilterTemplate/components/filter-settings';
import TimerLoader from '../../../components/base/Loader/TimerLoader';
import ExportInterface from '../../System/components/ExportInterface';
import TextSearchInput from '../../../components/fields/TextInput/TextSearchInput';

export default function VisitsPage() {
  const blockRef = useRef(null);
  const dispatch = useDispatch();
  const store = useStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const { updatePeriod } = useSelector(getSystemData);
  const trip_id = searchParams.get('trip_id');
  const {
    visitList,
    visitListMeta,
    loadingVisitsList,
  } = useSelector(getVisitData);

  const filterName = 'visits-filter';
  const [isOpenFilter, setOpenFilter] = useState(false);

  const { formatMessage } = useIntl();
  const loadData = opts => {
    const paginationOpts = getPaginationOpts(store.getState()[VISITS_MODULE].visitListMeta, opts);
    const requestData = {
      ...paginationOpts,
      filter: getValues(),
      sort,
    };
    const p1 = dispatch(loadVisitsList(requestData)).catch(responseLogWithNotification);
    return Promise.all([p1]);
  };
  const [setPeriod] = useUpdatePeriod(loadData, updatePeriod);
  useEffect(() => setPeriod(updatePeriod), [updatePeriod]);
  const defaultFilterValues = {};

  const {
    getValues, watch, control, reset, setValue,
  } = useForm({ defaultValues: getJsonFromStorage(filterName, defaultFilterValues) });
  useEffect(() => reset(getFilterData(filterName, defaultFilterValues)), []);
  useEffect(() => {
    const subscription = watch(values => {
      localStorage.setItem(filterName, JSON.stringify(values));
      loadData({ page: 1 });
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const moreFormatter = (_, item) => null;

  const [sort, setSort] = useState(getDefaultSort(visitColumns));

  const changeSort = fieldName => {
    if (sort && (sort === fieldName || sort === `-${fieldName}`)) {
      if (sort === `-${fieldName}`) {
        setSort(fieldName);
      } else if (sort === fieldName) {
        setSort('');
      }
    } else {
      setSort(`-${fieldName}`);
    }
  };

  const rowClassFormatter = item => {
    const classes = ['clickable-table-row'];
    return classes.join(' ');
  };

  useEffect(() => () => dispatch((closeAllPopupWrapper())), []);

  useEffect(() => { loadData(); }, [sort]);

  useEffect(() => {
    if (trip_id) {
      dispatch((addGlobalPopUpWrapper({ window: POPUP_LIST_NAME.VISIT_DETAIL, id: trip_id })));
    }
  }, [trip_id]);

  return (
    <div className="page visit-page">
      <TimerLoader global loading={loadingVisitsList} />
      <div className="page-title">
        <div className="title-text">{formatMessage({ id: 'menu.visits' })}</div>
        <FilterRow>
          <FieldDTO
            control={control}
            component={TextSearchInput}
            dtoObject={{
              name: 'search',
              type: String,
              labelKey: 'search',
            }}
            className="no-border"
          />
          <FieldDTO
            control={control}
            component={CustomSelectInput}
            dtoObject={{
              name: 'has_registry_vehicle',
              type: String,
              labelKey: 'vehicle_registry_presence',
            }}
            className="no-border"
            addPlaceholder
            options={VEHICLE_REGISTRY_PRESENCE_LIST}
          />
          <FieldDTO
            control={control}
            component={RangeDatePicker}
            dtoObject={{
              name: 'created_at',
              type: String,
              labelKey: 'created_at',
            }}
            usePortal
            placeholder="Дата события"
            changeFormat="YYYY-MM-DD"
            className="no-border"
          />
          <div className="filter-sep" />
          <ExportInterface page="trips" exporter="trips" getValues={getValues} />
          <div className="filter-sep" />
          <Button variant="second" onClick={() => setOpenFilter(true)}>
            <Icons customIcon className="file-download" width={24} height={24} icon="filter" />
          </Button>
          {isOpenFilter
            ? (
              <FilterModal
                control={control}
                onClose={() => setOpenFilter(false)}
                getValues={getValues}
                reset={reset}
                defaultValues={defaultFilterValues}
                page={PAGE_TYPE.VISITS}
              />
            )
            : null}
        </FilterRow>
      </div>
      <div className="page-content-wrapper">
        <div className="page-content" ref={blockRef}>
          <div className="grid-wrapper frame">
            <div className="custom-scroll-theme sticky-table-wrapper">
              <CustomizedGrid
                data={visitList}
                columns={visitColumns}
                name="visits-list"
                onDoubleClickByRow={item => {
                  dispatch((addGlobalPopUpWrapper({ window: POPUP_LIST_NAME.VISIT_DETAIL, id: item?.id })));
                }}
                rowClassFormatter={rowClassFormatter}
                settingsFormatter={moreFormatter}
                changeSort={changeSort}
                sort={sort}
              />
            </div>
          </div>
          <Pagination meta={visitListMeta} list={visitList} handleChangeMeta={loadData} />
        </div>
      </div>
    </div>
  );
}
